/**
 * A progress from 0 to 1 over `duration` milliseconds, frame by frame (feature plan 0072, the picker's draw).
 *
 * The progress comes from the CLOCK, not from counting frames: a hidden tab or a slow frame makes it jump
 * ahead, and it still ends on time. Eased out (cubic), so the looks slow down towards the result.
 *
 * `schedule` is the element's one frame slot (`cb => this.requestFrame(cb)` of `AbstractO2dElement`, which
 * also cancels the pending frame on disconnect); `now` the clock. Both injected, so tests run without
 * `requestAnimationFrame`. A duration of 0 (reduced motion) reports 1 and ends at once, synchronously.
 */
export interface FrameTweenOptions {
    readonly duration: number;
    readonly schedule: (callback: () => void) => void;
    readonly now: () => number;
    readonly onProgress: (progress: number) => void;
    readonly onDone: () => void;
}

export interface FrameTween {
    /** Stops without `onDone`; a frame still pending does nothing. */
    cancel(): void;
}

export function easeOutCubic(t: number): number {
    return 1 - (1 - t) ** 3;
}

export function startFrameTween({duration, schedule, now, onProgress, onDone}: FrameTweenOptions): FrameTween {
    let cancelled = false;
    const tween: FrameTween = {cancel: () => { cancelled = true; }};
    if (!(duration > 0)) {
        onProgress(1);
        onDone();
        return tween;
    }
    const startedAt = now();
    const frame = (): void => {
        if (cancelled) {
            return;
        }
        const t = Math.min(1, Math.max(0, (now() - startedAt) / duration));
        onProgress(easeOutCubic(t));
        if (t >= 1) {
            onDone();
        } else {
            schedule(frame);
        }
    };
    onProgress(0);
    schedule(frame);
    return tween;
}
